import React from 'react';

import { Checkbox } from '@swyftx/aviary/atoms/Checkbox';
import { Label } from '@swyftx/aviary/atoms/Label';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { RadioGroup } from '@swyftx/aviary/molecules/RadioGroup/RadioGroup';
import { RadioType } from '@swyftx/aviary/molecules/RadioGroup/RadioGroup';

import { Controller, useForm } from 'react-hook-form';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationSelector, useLoanApplicationService } from '../../xstate/LoanApplication.context';

interface IFormInputs {
  primaryPurpose: string;
  repaymentMethod: string;
  declaration?: boolean;
}

export const yesNoRadioGroup: RadioType[] = [
  { text: 'Yes', value: 'yes' },
  { text: 'No', value: 'no' },
];

const WhatIsYourLoanForStep: React.FC = () => {
  const loanApplicationService = useLoanApplicationService();
  const applicationData = useLoanApplicationSelector((state) => state.context.applicationData);

  const primaryPurposeCheckboxes: RadioType[] = [
    { text: 'Investments', value: 'investments' },
    { text: 'Home improvements', value: 'home' },
    { text: 'Holiday', value: 'holiday' },
    { text: 'Car purchase', value: 'car' },
    { text: 'Purchasing a gift', value: 'gift' },
    { text: 'Debt collection', value: 'debt' },
    { text: 'Home deposit or mortgage repayment', value: 'mortgage' },
  ];

  const repaymentMethodCheckboxes: RadioType[] = [
    { text: 'Salary or income', value: 'salary' },
    { text: 'Deposit more crypto', value: 'deposit' },
    { text: 'Use crypto security', value: 'security' },
  ];

  const initialPrimaryPurpose = applicationData.primaryPurpose || '';
  const initialRepaymentMethod = applicationData.repaymentMethod || '';

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<IFormInputs>({
    mode: 'onChange',
    defaultValues: {
      primaryPurpose: initialPrimaryPurpose,
      repaymentMethod: initialRepaymentMethod,
    },
  });

  const onSubmit = (data: IFormInputs) => {
    loanApplicationService.send({ type: 'NEXT', data });
  };

  return (
    <LoansStepContainer
      customTitle='Tell us about your loan intentions'
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={!isValid}
    >
      <main className='flex flex-col gap-32'>
        <Body>
          To help determine your suitability, we&apos;re required to know how you plan on using and repaying for your
          loan.
        </Body>

        {/* TODO: form label spacing vertical */}
        <div className='flex flex-col gap-8'>
          <Label>What is the primary purpose of the loan?</Label>
          <Controller
            name='primaryPurpose'
            control={control}
            rules={{ required: 'Please select the primary purpose of the loan' }}
            render={({ field }) => (
              <RadioGroup checkboxes={primaryPurposeCheckboxes} selected={field.value} onChange={field.onChange} />
            )}
          />
          {errors.primaryPurpose && (
            <Body size='small' color='error'>
              {errors.primaryPurpose.message?.toString()}
            </Body>
          )}
        </div>

        <div className='flex flex-col gap-8'>
          <Label>How do you plan to repay loan amount?</Label>
          <Controller
            name='repaymentMethod'
            control={control}
            rules={{ required: 'Please select your repayment plan' }}
            render={({ field }) => (
              <RadioGroup checkboxes={repaymentMethodCheckboxes} selected={field.value} onChange={field.onChange} />
            )}
          />
          {errors.repaymentMethod && (
            <Body size='small' color='error'>
              {errors.repaymentMethod.message?.toString()}
            </Body>
          )}
        </div>

        <FlexLayout direction='row' alignItems='center' spacing={8}>
          <Controller
            name='declaration'
            control={control}
            rules={{ required: 'You must declare to continue' }}
            render={({ field }) => (
              <Checkbox
                className='h-20 w-20 border-2 border-color-border-main'
                id='declaration-checkbox'
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                }}
              />
            )}
          />
          <Label htmlFor='declaration-checkbox'>
            I declare that the proceeds of this loan are not for the purchase of cryptocurrency, cryptocurrency
            derivatives or any other financial product.
          </Label>
        </FlexLayout>
      </main>
    </LoansStepContainer>
  );
};

export { WhatIsYourLoanForStep };
